import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, Trash2, Building2, Calendar, User, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { api, getPropertyCoverImageUrl, PROPERTY_IMAGE_PLACEHOLDER, getUploadsPublicUrl } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import type { MaintenanceStatus, MaintenanceType } from './PropertyManagementList';
import { MAINTENANCE_TYPE_CONFIG } from './PropertyManagementList';
import { formatDateLong } from '../../utils/date';
import { GenericTimeline, type TimelineEvent } from '../../components/ui/GenericTimeline';

function StatusBadge({ status }: { status: MaintenanceStatus }) {
  const config: Record<MaintenanceStatus, { label: string; className: string }> = {
    pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
    in_progress: { label: 'En progreso', className: 'bg-blue-100 text-blue-800' },
    completed: { label: 'Completado', className: 'bg-green-100 text-green-800' },
  };
  const { label, className } = config[status] ?? config.pending;
  return (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

function TypeBadge({ type }: { type: MaintenanceType }) {
  const { label, className } = MAINTENANCE_TYPE_CONFIG[type] ?? MAINTENANCE_TYPE_CONFIG.general;
  return (
    <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${className}`}>
      {label}
    </span>
  );
}

export const PropertyManagementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    type: 'general' as MaintenanceType,
    performedBy: '',
    performedAt: '',
    status: 'pending' as MaintenanceStatus,
    notes: '',
  });
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [savedImageUrls, setSavedImageUrls] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingPreviews, setPendingPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const { hasPermission } = usePermissions();
  const canUpdateMaintenance =
    hasPermission('update_property_management') || hasPermission('manage_property_management');
  const canDeleteMaintenance = hasPermission('delete_property_management');

  const { data: maintenanceData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['property-maintenance', id],
    queryFn: () => api.getPropertyMaintenance(id!),
    enabled: !!id,
    staleTime: 60_000,
  });

  const propertyId = maintenanceData?.propertyId ?? maintenanceData?.properties?.id;
  const { data: historyData } = useQuery({
    queryKey: ['property-maintenances-history', propertyId],
    queryFn: () => api.getPropertyMaintenancesByProperty(propertyId!),
    enabled: !!propertyId,
    staleTime: 60_000,
  });

  const { data: maintenanceUsers = [] } = useQuery({
    queryKey: ['users', 'by-role', 'Mantenimiento'],
    queryFn: () => api.getMaintenanceUsers(),
    staleTime: 5 * 60_000,
    enabled: isEditModalOpen && canUpdateMaintenance,
  });

  const performedByMatchesTechnician = useMemo(() => {
    const pb = editForm.performedBy.trim();
    if (!pb) return true;
    return maintenanceUsers.some((u) => u.name.trim() === pb);
  }, [editForm.performedBy, maintenanceUsers]);

  const updateMutation = useMutation({
    mutationFn: (payload: typeof editForm & { images?: string[] }) =>
      api.updatePropertyMaintenance(id!, {
        title: payload.title,
        description: payload.description || undefined,
        type: payload.type,
        performedBy: payload.performedBy,
        performedAt: payload.performedAt,
        status: payload.status,
        notes: payload.notes || undefined,
        images: payload.images,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-maintenance', id] });
      queryClient.invalidateQueries({ queryKey: ['property-maintenances'] });
      queryClient.invalidateQueries({ queryKey: ['property-maintenances-history', propertyId] });
      setIsEditModalOpen(false);
      setMutationError(null);
      setPendingFiles([]);
      setPendingPreviews([]);
    },
    onError: (err: Error) => setMutationError(err.message || 'Error al actualizar'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deletePropertyMaintenance(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['property-maintenances'] });
      navigate('/property-management');
    },
    onError: (err: Error) => setMutationError(err.message || 'Error al eliminar'),
  });

  const property = maintenanceData?.properties ?? maintenanceData?.property;
  const maintenance = maintenanceData
    ? {
        id: maintenanceData.id,
        title: maintenanceData.title,
        description: maintenanceData.description,
        type: (maintenanceData.type || 'general') as MaintenanceType,
        performedBy: maintenanceData.performedBy,
        performedAt: maintenanceData.performedAt,
        cost: maintenanceData.cost != null ? Number(maintenanceData.cost) : undefined,
        status: (maintenanceData.status || 'pending') as MaintenanceStatus,
        notes: maintenanceData.notes,
        images: Array.isArray(maintenanceData.images) ? maintenanceData.images : [],
      }
    : null;

  const historyList = useMemo(() => {
    const list = Array.isArray(historyData) ? historyData : [];
    return list
      .map((h: any) => ({
        id: h.id,
        title: h.title,
        performedBy: h.performedBy,
        performedAt: h.performedAt,
        createdAt: h.createdAt,
        status: (h.status || 'pending') as MaintenanceStatus,
      }))
      .sort((a: any, b: any) => {
        const da = new Date(a.performedAt).getTime();
        const db = new Date(b.performedAt).getTime();
        return db - da;
      });
  }, [historyData]);

  const propertyMaintenanceTimelineEvents: TimelineEvent[] = useMemo(
    () =>
      historyList.map((m) => ({
        id: m.id,
        date: m.performedAt || m.createdAt || new Date().toISOString(),
        type: m.status,
        title: m.title,
        description: `Asignado a: ${m.performedBy?.trim() ? m.performedBy : '—'}`,
        icon:
          m.status === 'completed' ? 'check-circle' : m.status === 'in_progress' ? 'play-circle' : 'clock',
      })),
    [historyList],
  );

  const error =
    queryError != null
      ? (queryError instanceof Error ? queryError.message : String(queryError))
      : null;

  const openEditModal = () => {
    if (!maintenanceData) return;
    setEditForm({
      title: maintenanceData.title ?? '',
      description: maintenanceData.description ?? '',
      type: (maintenanceData.type || 'general') as MaintenanceType,
      performedBy: maintenanceData.performedBy ?? '',
      performedAt: maintenanceData.performedAt
        ? new Date(maintenanceData.performedAt).toISOString().split('T')[0]
        : '',
      status: (maintenanceData.status || 'pending') as MaintenanceStatus,
      notes: maintenanceData.notes ?? '',
    });
    setMutationError(null);
    setSavedImageUrls(Array.isArray(maintenanceData.images) ? [...maintenanceData.images] : []);
    setPendingFiles([]);
    setPendingPreviews([]);
    setIsEditModalOpen(true);
  };

  const handleEditFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files]);
    files.forEach((f) => {
      const reader = new FileReader();
      reader.onload = () => setPendingPreviews((prev) => [...prev, reader.result as string]);
      reader.readAsDataURL(f);
    });
    e.target.value = '';
  };

  const removeSavedImage = (idx: number) => {
    setSavedImageUrls((prev) => prev.filter((_, i) => i !== idx));
  };

  const removePendingImage = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
    setPendingPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm.title.trim() || !editForm.performedBy.trim() || !editForm.performedAt) {
      setMutationError('Completa título, técnico asignado y fecha.');
      return;
    }
    setMutationError(null);
    let mergedImages = [...savedImageUrls];
    try {
      if (pendingFiles.length > 0) {
        setUploading(true);
        const res = await api.uploadPropertyManagementImages(pendingFiles);
        mergedImages = [...mergedImages, ...(res.fileUrls ?? [])];
      }
      await updateMutation.mutateAsync({ ...editForm, images: mergedImages });
    } catch (err) {
      setMutationError(err instanceof Error ? err.message : 'Error al guardar');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !maintenance) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">{error || 'Mantenimiento no encontrado'}</p>
        <Button onClick={() => navigate('/property-management')} className="mt-4">
          Volver al listado
        </Button>
      </div>
    );
  }

  const performedAtStr = maintenance.performedAt ? formatDateLong(maintenance.performedAt) : '—';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Button variant="ghost" onClick={() => navigate('/property-management')} className="self-start flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Volver al listado
        </Button>
        {(canUpdateMaintenance || canDeleteMaintenance) && (
          <div className="flex gap-2">
            {canUpdateMaintenance && (
              <Button variant="secondary" onClick={openEditModal} className="flex items-center gap-2">
                <Edit className="w-4 h-4" />
                Editar
              </Button>
            )}
            {canDeleteMaintenance && (
              <Button
                variant="secondary"
                onClick={() => setDeleteConfirmId(id!)}
                className="flex items-center gap-2 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar
              </Button>
            )}
          </div>
        )}
      </div>

      {mutationError && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{mutationError}</div>
      )}

      <Card title="Propiedad">
        <div className="flex flex-wrap gap-4 items-start">
          <img
            src={property?.coverImage ? getPropertyCoverImageUrl(property.coverImage) : PROPERTY_IMAGE_PLACEHOLDER}
            alt=""
            className="w-24 h-24 rounded-lg object-cover"
          />
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-primary" />
              {property?.name ?? '—'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">{property?.address ?? '—'}</p>
            {property?.city && (
              <p className="text-sm text-gray-500">{property.city}</p>
            )}
          </div>
        </div>
      </Card>

      <Card title="Detalle del mantenimiento">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500">Título</h4>
            <p className="text-gray-900 font-medium">{maintenance.title}</p>
          </div>
          {maintenance.description && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Descripción</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{maintenance.description}</p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              <div>
                <h4 className="text-sm font-medium text-gray-500">Asignado a</h4>
                <p className="text-gray-900">{maintenance.performedBy}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <div>
                <h4 className="text-sm font-medium text-gray-500">Fecha</h4>
                <p className="text-gray-900">{performedAtStr}</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Tipo</h4>
              <TypeBadge type={(maintenance as any).type ?? 'general'} />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-500">Estado</h4>
              <StatusBadge status={maintenance.status} />
            </div>
            {maintenance.cost != null && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Costo</h4>
                  <p className="text-gray-900">${Number(maintenance.cost).toLocaleString()}</p>
                </div>
              </div>
            )}
          </div>
          {maintenance.notes && (
            <div>
              <h4 className="text-sm font-medium text-gray-500">Notas</h4>
              <p className="text-gray-700 whitespace-pre-wrap">{maintenance.notes}</p>
            </div>
          )}
        </div>
      </Card>

      {maintenance.images && maintenance.images.length > 0 && (
        <Card title="Imágenes del mantenimiento">
          <div className="flex flex-wrap gap-3">
          {maintenance.images.map((url: string, i: number) => {
              const src = getUploadsPublicUrl(url);
              const href = src || url;
              return (
              <a
                key={i}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <img
                  src={src || url}
                  alt={`Mantenimiento ${i + 1}`}
                  className="w-24 h-24 object-cover rounded-lg border border-gray-200 hover:opacity-90"
                />
              </a>
            );
            })}
          </div>
        </Card>
      )}

      <Card title="Historial de mantenimientos de esta propiedad">
        <GenericTimeline
          events={propertyMaintenanceTimelineEvents}
          colorMap={{
            pending: 'bg-amber-500',
            in_progress: 'bg-blue-500',
            completed: 'bg-green-500',
          }}
          emptyMessage="No hay mantenimientos registrados para esta propiedad."
        />
      </Card>

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setPendingFiles([]);
          setPendingPreviews([]);
          setMutationError(null);
        }}
        title="Editar mantenimiento"
        size="lg"
      >
        <form onSubmit={handleEditSubmit} className="space-y-4">
          {mutationError && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">{mutationError}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <input
              type="text"
              value={editForm.title}
              onChange={(e) => setEditForm((d) => ({ ...d, title: e.target.value }))}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm((d) => ({ ...d, description: e.target.value }))}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asignar a *</label>
              <select
                value={editForm.performedBy}
                onChange={(e) => setEditForm((d) => ({ ...d, performedBy: e.target.value }))}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="">Seleccionar técnico...</option>
                {!performedByMatchesTechnician && editForm.performedBy.trim() !== '' && (
                  <option value={editForm.performedBy}>
                    {editForm.performedBy} (registro anterior)
                  </option>
                )}
                {maintenanceUsers.map((user) => (
                  <option key={user.id} value={user.name}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
              <input
                type="date"
                value={editForm.performedAt}
                onChange={(e) => setEditForm((d) => ({ ...d, performedAt: e.target.value }))}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de mantenimiento</label>
              <select
                value={editForm.type}
                onChange={(e) => setEditForm((d) => ({ ...d, type: e.target.value as MaintenanceType }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="general">General</option>
                <option value="desazolve">Desazolve</option>
                <option value="impermeabilizacion">Impermeabilización</option>
                <option value="preventivo">Preventivo</option>
                <option value="correctivo">Correctivo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={editForm.status}
                onChange={(e) => setEditForm((d) => ({ ...d, status: e.target.value as MaintenanceStatus }))}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
              >
                <option value="pending">Pendiente</option>
                <option value="in_progress">En progreso</option>
                <option value="completed">Completado</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm((d) => ({ ...d, notes: e.target.value }))}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fotos / Evidencia</label>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleEditFileChange}
              className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary"
            />
            {(savedImageUrls.length > 0 || pendingPreviews.length > 0) && (
              <div className="flex gap-2 flex-wrap mt-2">
                {savedImageUrls.map((url, i) => (
                  <div key={`saved-${i}`} className="relative">
                    <img
                      src={getUploadsPublicUrl(url) || url}
                      alt=""
                      className="w-20 h-20 object-cover rounded border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removeSavedImage(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      aria-label="Quitar imagen"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {pendingPreviews.map((src, i) => (
                  <div key={`pending-${i}`} className="relative">
                    <img src={src} alt="" className="w-20 h-20 object-cover rounded border border-gray-200" />
                    <button
                      type="button"
                      onClick={() => removePendingImage(i)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      aria-label="Quitar imagen nueva"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsEditModalOpen(false);
                setPendingFiles([]);
                setPendingPreviews([]);
                setMutationError(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={updateMutation.isPending || uploading}>
              {uploading || updateMutation.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDeleteModal
        isOpen={!!deleteConfirmId}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteConfirmId(null);
        }}
        onConfirm={() => {
          deleteMutation.mutate(undefined, {
            onSettled: () => setDeleteConfirmId(null),
          });
        }}
        title="Eliminar mantenimiento"
        message="¿Seguro que deseas eliminar este registro de mantenimiento?"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
};
